import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d60jsdgiku00ff9vl6u0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const offices = [
    {
      city: 'Dallas',
      country: 'United States',
      address: 'Dallas, TX',
      icon: 'ri-map-pin-line',
      image: 'https://public.readdy.ai/ai/img_res/9c90fd522ed6160e5a78a91185c8c08a.jpg'
    },
    {
      city: 'Calgary',
      country: 'Canada',
      address: 'Calgary, AB',
      icon: 'ri-map-pin-line',
      image: 'https://readdy.ai/api/search-image?query=Calgary%20Alberta%20Canada%20downtown%20skyline%20with%20modern%20office%20towers%20and%20Rocky%20Mountains%20in%20background%20representing%20business%20center%20and%20corporate%20location%20in%20bright%20clear%20weather&width=600&height=400&seq=calgary001&orientation=landscape'
    },
    {
      city: 'London',
      country: 'United Kingdom',
      address: 'London, UK',
      icon: 'ri-map-pin-line',
      image: 'https://readdy.ai/api/search-image?query=London%20United%20Kingdom%20modern%20business%20district%20with%20contemporary%20office%20buildings%20and%20urban%20architecture%20representing%20corporate%20headquarters%20and%20financial%20center%20in%20daylight&width=600&height=400&seq=london001&orientation=landscape'
    },
    {
      city: 'Bangalore',
      country: 'India',
      address: 'Bangalore, IN',
      icon: 'ri-map-pin-line',
      image: 'https://readdy.ai/api/search-image?query=Bangalore%20India%20modern%20technology%20park%20with%20contemporary%20office%20buildings%20and%20IT%20campus%20representing%20tech%20hub%20and%20business%20center%20in%20bright%20daylight%20with%20clean%20professional%20aesthetic&width=600&height=400&seq=bangalore001&orientation=landscape'
    }
  ];

  const contactMethods = [
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      detail: 'info@clearpathconsulting-global.com',
      description: 'Send us an email anytime',
      link: 'mailto:info@clearpathconsulting-global.com'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      detail: '+1 (403) 903-5343',
      description: 'Mon-Fri from 8am to 5pm',
      link: 'tel:+14039035343'
    },
    {
      icon: 'ri-linkedin-fill',
      title: 'LinkedIn',
      detail: 'Connect with us',
      description: 'Follow our company updates',
      link: 'https://www.linkedin.com/company/clear-path-consulting-inc/?viewAsMember=true'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-72 pb-40 bg-gradient-to-br from-sky-600 to-sky-700">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=professional%20business%20executives%20shaking%20hands%20in%20modern%20corporate%20office%20environment%20with%20confident%20smiles%20representing%20successful%20partnership%20collaboration%20and%20business%20agreement%20with%20bright%20natural%20lighting%20clean%20contemporary%20setting%20and%20professional%20attire%20suitable%20for%20contact%20page%20hero%20section%20background&width=1920&height=600&seq=contacthero002&orientation=landscape" 
            alt="" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Get in Touch</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Ready to transform your business with ServiceNow? Our team of experts is here to help you achieve your goals.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.link}
                target={method.link.startsWith('http') ? '_blank' : undefined}
                rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg p-8 text-center hover:shadow-lg hover:from-slate-600 hover:to-slate-700 transition-all group cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-sky-400 text-slate-800 rounded-full mx-auto mb-4 group-hover:bg-sky-300 transition-colors">
                  <i className={`${method.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{method.title}</h3>
                <p className="text-sky-400 font-semibold mb-2">{method.detail}</p>
                <p className="text-sm text-white/80">{method.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Info */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Let's Start a Conversation
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're looking to implement ServiceNow for the first time, optimize your existing platform, or need expert consulting services, we're here to help.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-sky-100 text-sky-600 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">Quick Response</h4>
                    <p className="text-sm text-gray-600">We typically respond to inquiries within 24 hours during business days.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-sky-100 text-sky-600 rounded-lg flex-shrink-0">
                    <i className="ri-user-star-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">Expert Consultation</h4>
                    <p className="text-sm text-gray-600">Connect directly with our ServiceNow certified professionals.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-sky-100 text-sky-600 rounded-lg flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">Confidential & Secure</h4>
                    <p className="text-sm text-gray-600">Your information is protected and will never be shared with third parties.</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-gradient-to-br from-sky-600 to-sky-700 rounded-lg text-white">
                <h4 className="text-xl font-bold mb-3">Need Immediate Assistance?</h4>
                <p className="text-sm text-white/90 mb-4">
                  For urgent matters, please call us directly at:
                </p>
                <a 
                  href="tel:+14039035343"
                  className="text-2xl font-bold hover:text-sky-200 transition-colors"
                >
                  +1 (403) 903-5343
                </a>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                    placeholder="john.doe@company.com"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                    placeholder="Your Company"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Interest *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select a service</option>
                    <option value="ITSM">IT Service Management (ITSM)</option>
                    <option value="SecOps">Security Operations (SecOps)</option>
                    <option value="ITOM">IT Operations Management (ITOM)</option>
                    <option value="CSM">Customer Service Management (CSM)</option>
                    <option value="HAM">Hardware Asset Management (HAM)</option>
                    <option value="SAM">Software Asset Management (SAM)</option>
                    <option value="HRSD">HR Service Delivery (HRSD)</option>
                    <option value="SPM">Strategic Portfolio Management (SPM)</option>
                    <option value="Implementation">ServiceNow Implementation</option>
                    <option value="Optimization">Platform Optimization</option>
                    <option value="Consulting">General Consulting</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 flex items-center">
                      <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                      Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 flex items-center">
                      <i className="ri-error-warning-line text-xl mr-2"></i>
                      Sorry, there was an error sending your message. Please try again or contact us directly.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Global Presence</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              With offices across four continents, we're positioned to serve clients worldwide with local expertise and global capabilities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {offices.map((office, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-lg mb-4 h-48">
                  <img 
                    src={office.image} 
                    alt={`${office.city} Office`}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-xl font-bold text-white mb-1">{office.city}</h4>
                    <p className="text-sm text-white/90">{office.country}</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-600">
                  <i className={`${office.icon} text-sky-600 mr-2`}></i>
                  <span className="text-sm">{office.address}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-sky-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Find Us on the Map</h2>
            <p className="text-lg text-white/80">Headquarters in Calgary, Alberta, Canada</p>
          </div>
          
          <div className="rounded-lg overflow-hidden">
            <img
              src="https://static.readdy.ai/image/ff0511a30dd8d80be2e4d044f17576be/5ab3b7265d5e63193f6b399c08d10322.webp"
              alt="ClearPath Consulting Global Office Locations"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Join the hundreds of organizations that trust ClearPath Consulting for their ServiceNow needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:info@clearpathconsulting-global.com"
              className="inline-block bg-sky-600 hover:bg-sky-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap"
            >
              Email Us Directly
            </a>
            <a 
              href="tel:+14039035343"
              className="inline-block bg-slate-700 hover:bg-slate-800 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Back to Top Button */}
      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-12 h-12 flex items-center justify-center bg-sky-500 hover:bg-sky-600 text-white rounded-full shadow-lg transition-colors z-40"
      >
        <i className="ri-arrow-up-line text-xl"></i>
      </button>
    </div>
  );
}
